// AdminCuestionarios — gestor de plantillas CEP y CAD (100% editables).
// El super admin ve todas las plantillas; el admin operativo gestiona contenido
// (la edición está limitada al super-admin para mantener la integridad de las
// plantillas oficiales JASQ; el admin operativo puede verlas y proponer cambios
// vía botón "duplicar" — feature futura).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpenCheck, Sparkles, ChevronDown, ChevronRight, Pencil, Plus, Trash2, X,
  GripVertical, Save, Quote, Upload, Download,
  Folder, FolderPlus, FolderOpen, ArrowUp, ArrowDown, MoreVertical, Edit3, Move,
} from "lucide-react";

const SCALES = [
  { id: "likert_agree", label: "Acuerdo (1-5)" },
  { id: "likert_freq", label: "Frecuencia (1-5)" },
  { id: "scale_1_10", label: "Escala 1-10" },
  { id: "open", label: "Abierta / Texto" },
];

const QTYPE_OPTIONS = [
  { id: "likert", label: "Likert" },
  { id: "scale_1_10", label: "Escala 1-10" },
  { id: "numeric", label: "Numérica" },
  { id: "open", label: "Texto abierto" },
];

export default function AdminCuestionarios() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState("cep"); // "cep" | "cad"

  const isSuper = !!user?.is_super_admin;
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeCode, setActiveCode] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [tplRes, fldRes] = await Promise.all([
        api.get("/questionnaire-templates"),
        api.get("/questionnaire-folders"),
      ]);
      setTemplates(tplRes.data);
      setFolders(fldRes.data);
    } catch (e) { toast.error("No fue posible cargar plantillas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // refresh tras importación
  useEffect(() => {
    const h = () => load();
    window.addEventListener("tpl-refresh", h);
    return () => window.removeEventListener("tpl-refresh", h);
  }, []); // eslint-disable-line

  const visible = templates.filter((t) => t.kind === activeTab);
  const active = visible.find((t) => t.code === activeCode);

  // Cuando cambia de pestaña, autoseleccionar la primera del tipo activo
  useEffect(() => {
    if (visible.length && (!active || active.kind !== activeTab)) {
      setActiveCode(visible[0].code);
    } else if (!visible.length) {
      setActiveCode(null);
    }
  }, [activeTab, templates.length]); // eslint-disable-line

  // ---- Acciones carpetas / orden / export / import nuevo ----
  const handleCreateFolder = async () => {
    const name = prompt(`Nombre de la nueva carpeta ${activeTab.toUpperCase()}:`);
    if (!name || !name.trim()) return;
    try {
      await api.post("/questionnaire-folders", { name: name.trim(), kind: activeTab });
      toast.success("Carpeta creada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };
  const handleRenameFolder = async (folder) => {
    const name = prompt("Nuevo nombre:", folder.name);
    if (!name || !name.trim() || name === folder.name) return;
    try {
      await api.patch(`/questionnaire-folders/${folder.id}`, { name: name.trim() });
      toast.success("Carpeta renombrada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };
  const handleDeleteFolder = async (folder) => {
    if (!confirm(`¿Eliminar carpeta "${folder.name}"? Las plantillas dentro pasarán a "Sin carpeta".`)) return;
    try {
      await api.delete(`/questionnaire-folders/${folder.id}`);
      toast.success("Carpeta eliminada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };
  const moveTemplate = async (tpl, direction) => {
    try {
      await api.post(`/questionnaire-templates/${tpl.code}/reorder/${direction}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };
  const assignToFolder = async (tpl, folderId) => {
    try {
      await api.post(`/questionnaire-templates/${tpl.code}/move`, { folder_id: folderId });
      toast.success(folderId ? "Movido a la carpeta" : "Sacado de la carpeta");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };
  const exportTemplate = async (tpl, fmt) => {
    try {
      const r = await api.get(`/questionnaire-templates/${tpl.code}/export`, { params: { fmt }, responseType: "blob" });
      const ext = fmt;
      const mime = fmt === "json" ? "application/json"
        : fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `cuestionario_${tpl.code}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al exportar"); }
  };

  const onDelete = async () => {
    if (!active) return;
    if (!confirm(`¿Eliminar la plantilla "${active.title}" (${active.code})?\nEsta acción es permanente.`)) return;
    try {
      await api.delete(`/questionnaire-templates/${active.code}`);
      toast.success("Plantilla eliminada");
      setActiveCode(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      const isSuper = !!user?.is_super_admin;
      // Si bloquea por respuestas, ofrecer eliminación forzada (sólo Super Admin)
      if (isSuper && /respuesta\(s\)/i.test(String(detail))) {
        if (confirm(`${detail}\n\n¿Deseas FORZAR la eliminación y purgar TODAS las respuestas? Esta acción es irreversible.`)) {
          try {
            await api.delete(`/questionnaire-templates/${active.code}?force=true`);
            toast.success("Plantilla eliminada (con purga de respuestas)");
            setActiveCode(null);
            await load();
            return;
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al forzar la eliminación");
            return;
          }
        }
      }
      toast.error(formatApiErrorDetail(detail) || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-cuestionarios-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#F5B82E]">Módulo M2 · M3</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Cuestionarios CEP · CAD</h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Plantillas 100% editables desde un solo editor unificado.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="tpl-create-btn" onClick={() => setShowCreate(true)}
            className="jasq-btn-primary inline-flex items-center gap-2">
            <Plus size={14} /> Nueva plantilla
          </button>
          <button data-testid="folder-create-btn" onClick={handleCreateFolder}
            title="Crear una nueva carpeta para agrupar plantillas"
            className="jasq-btn-ghost inline-flex items-center gap-2">
            <FolderPlus size={14} /> Nueva carpeta
          </button>
          {active && (
            <div className="flex gap-1">
              <button onClick={() => exportTemplate(active, "docx")} data-testid="tpl-export-docx"
                title="Descargar plantilla en Word" className="jasq-btn-ghost inline-flex items-center gap-1 text-xs">
                <Download size={12} /> Word
              </button>
              <button onClick={() => exportTemplate(active, "xlsx")} data-testid="tpl-export-xlsx"
                title="Descargar plantilla en Excel" className="jasq-btn-ghost inline-flex items-center gap-1 text-xs">
                <Download size={12} /> Excel
              </button>
            </div>
          )}
          {active && (
            <button data-testid="tpl-delete-btn" onClick={onDelete}
              className="jasq-btn-ghost inline-flex items-center gap-2 text-[#A52258]">
              <Trash2 size={14} /> Eliminar
            </button>
          )}
        </div>
      </div>

      {/* Tabs CEP / CAD */}
      <div className="flex gap-2 border-b-2 border-[#E1E5F0]" data-testid="tpl-kind-tabs">
        <TabBtn
          active={activeTab === "cep"}
          onClick={() => setActiveTab("cep")}
          icon={<Sparkles size={14} />}
          label="CEP"
          subtitle="Cuestionario Emotivo-Perceptual"
          count={templates.filter((t) => t.kind === "cep").length}
          testId="tab-cep"
        />
        <TabBtn
          active={activeTab === "cad"}
          onClick={() => setActiveTab("cad")}
          icon={<BookOpenCheck size={14} />}
          label="CAD"
          subtitle="Cuestionario Académico Diagnóstico"
          count={templates.filter((t) => t.kind === "cad").length}
          testId="tab-cad"
        />
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="space-y-4">
          <TemplateList
            title={activeTab.toUpperCase()}
            description={activeTab === "cep" ? "Cuestionario Emotivo-Perceptual" : "Diagnóstico técnico por nivel"}
            icon={activeTab === "cep" ? <Sparkles size={14} /> : <BookOpenCheck size={14} />}
            items={visible}
            folders={folders.filter((f) => (f.kind || "cep") === activeTab)}
            active={activeCode}
            onSelect={setActiveCode}
            onMove={moveTemplate}
            onAssignFolder={assignToFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
          />
        </aside>

        <section>
          {loading && <div className="jasq-card p-8 text-center text-[#6E7591]">Cargando…</div>}
          {!loading && !active && (
            <div className="jasq-card p-8 text-center">
              <p className="text-sm text-[#6E7591]">
                {visible.length === 0
                  ? `No hay plantillas ${activeTab.toUpperCase()} todavía. Crea la primera con "Nueva plantilla".`
                  : "Selecciona una plantilla a la izquierda."}
              </p>
            </div>
          )}
          {!loading && active && (
            <TemplateViewer
              template={active}
              canEdit={isSuper}
              onEdit={() => navigate(`/admin/cad-editor/${active.code}`)}
            />
          )}
        </section>
      </div>

      {showCreate && (
        <CreateTemplateModal
          defaultKind={activeTab}
          onClose={() => setShowCreate(false)}
          onCreated={(tpl) => {
            setShowCreate(false);
            setActiveTab(tpl.kind);
            setActiveCode(tpl.code);
            load();
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, subtitle, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`-mb-[2px] px-4 py-3 border-b-4 transition-colors text-left ${
        active ? "border-[#A43790] text-[#1F2547]" : "border-transparent text-[#6E7591] hover:text-[#1F2547]"
      }`}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-extrabold">
        {icon} {label}
        <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#A43790] text-white" : "bg-[#F2F4FA] text-[#6E7591]"}`}>{count}</span>
      </div>
      <p className="text-[10px] text-[#6E7591] mt-0.5">{subtitle}</p>
    </button>
  );
}

function CreateTemplateModal({ defaultKind = "cad", onClose, onCreated }) {
  const [form, setForm] = useState({
    code: "", kind: defaultKind, title: "", subtitle: "",
    academic_level: "", scope: "global",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/questionnaire-templates", {
        ...form,
        code: form.code.toUpperCase().trim(),
      });
      toast.success(`Plantilla ${data.code} creada`);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="create-tpl-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Nueva plantilla</h3>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <Field label="Código único (ej: CAD-P5)">
            <input data-testid="tpl-code"
              value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="CAD-P5"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-mono uppercase" />
          </Field>
          <Field label="Tipo">
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "cep", label: "CEP · Emotivo-Perceptual" },
                { key: "cad", label: "CAD · Académico Diagnóstico" },
              ].map((t) => (
                <button key={t.key} type="button" data-testid={`tpl-kind-${t.key}`}
                  onClick={() => setForm({ ...form, kind: t.key })}
                  className={`p-2.5 rounded-xl border-2 text-xs font-bold ${form.kind === t.key ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0]"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Título visible">
            <input data-testid="tpl-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Cuestionario CAD — 5° de Primaria"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790]" />
          </Field>
          <Field label="Nivel académico (opcional)">
            <input value={form.academic_level} onChange={(e) => setForm({ ...form, academic_level: e.target.value })}
              placeholder="5° de Primaria"
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790]" />
          </Field>
          <Field label="Subtítulo (opcional)">
            <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790]" />
          </Field>
          <p className="text-[11px] text-[#6E7591]">Después de crearla, podrás agregar bloques y preguntas desde el editor visual.</p>
        </div>
        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={saving || !form.code || !form.title}
            data-testid="tpl-submit" className="jasq-btn-primary">
            {saving ? "Creando…" : "Crear plantilla"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function TemplateList({ title, description, icon, items, folders, active, onSelect, onMove, onAssignFolder, onRenameFolder, onDeleteFolder }) {
  const [openFolders, setOpenFolders] = useState({});
  const [menuOpen, setMenuOpen] = useState(null);

  // Agrupar plantillas por carpeta. `null` = sin carpeta.
  const byFolder = items.reduce((acc, t) => {
    const k = t.folder_id || "_unfiled";
    if (!acc[k]) acc[k] = [];
    acc[k].push(t);
    return acc;
  }, {});
  for (const k of Object.keys(byFolder)) {
    byFolder[k].sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  }

  const toggleFolder = (id) => setOpenFolders((o) => ({ ...o, [id]: !o[id] }));

  const renderTpl = (t, list, i) => (
    <div
      key={t.code}
      data-testid={`tpl-${t.code}`}
      className={`w-full px-3 py-2 rounded-lg text-xs font-bold transition-colors flex items-start gap-2 ${
        active === t.code ? "bg-[#1F2547] text-white" : "text-[#4C5478] hover:bg-[#F2F4FA]"
      }`}
    >
      <div className="flex-1 cursor-pointer min-w-0" onClick={() => onSelect(t.code)}>
        <div className="flex items-center justify-between">
          <span className="truncate">{t.code}</span>
          <span className={`text-[10px] shrink-0 ml-1 ${active === t.code ? "text-[#FFCC28]" : "text-[#6E7591]"}`}>
            {t.blocks?.length || 0} bloques
          </span>
        </div>
        <p className={`text-[11px] mt-0.5 font-medium truncate ${active === t.code ? "text-[#E1E5F0]" : "text-[#6E7591]"}`}>
          {t.title}
        </p>
      </div>
      <div className="flex flex-col gap-0.5 shrink-0">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMove(t, "up"); }}
          disabled={i === 0}
          className={`p-0.5 rounded ${active === t.code ? "text-[#FFCC28] hover:text-white" : "text-[#6E7591] hover:text-[#A43790]"} disabled:opacity-30`}
          title="Subir"
          data-testid={`tpl-${t.code}-up`}
        >
          <ArrowUp size={11} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMove(t, "down"); }}
          disabled={i === list.length - 1}
          className={`p-0.5 rounded ${active === t.code ? "text-[#FFCC28] hover:text-white" : "text-[#6E7591] hover:text-[#A43790]"} disabled:opacity-30`}
          title="Bajar"
          data-testid={`tpl-${t.code}-down`}
        >
          <ArrowDown size={11} />
        </button>
      </div>
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === t.code ? null : t.code); }}
          className={`p-0.5 rounded ${active === t.code ? "text-[#FFCC28] hover:text-white" : "text-[#6E7591] hover:text-[#A43790]"}`}
          title="Mover a carpeta"
          data-testid={`tpl-${t.code}-menu`}
        >
          <MoreVertical size={12} />
        </button>
        {menuOpen === t.code && (
          <div className="absolute right-0 top-5 z-20 bg-white border-2 border-[#E1E5F0] rounded-xl shadow-xl min-w-[180px] py-1">
            <p className="text-[9px] uppercase tracking-wider font-extrabold text-[#6E7591] px-3 py-1 border-b border-[#E1E5F0]">Mover a…</p>
            <button
              type="button"
              onClick={() => { onAssignFolder(t, null); setMenuOpen(null); }}
              className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-[#F2F4FA] text-[#1F2547] font-bold"
            >
              📤 Sin carpeta
            </button>
            {folders.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { onAssignFolder(t, f.id); setMenuOpen(null); }}
                className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-[#F2F4FA] text-[#1F2547] font-bold inline-flex items-center gap-1.5"
              >
                <Folder size={11} style={{ color: f.color || "#A43790" }} /> {f.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="jasq-card p-3" onClick={() => setMenuOpen(null)}>
      <div className="px-2 pb-2 mb-2 border-b border-[#E1E5F0]">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-extrabold text-[#1F2547]">
          {icon}{title}
        </div>
        <p className="text-[11px] text-[#6E7591] mt-0.5">{description}</p>
      </div>
      <div className="space-y-1">
        {/* Carpetas (filtradas por las que tienen plantillas de este tipo o las globales) */}
        {folders.map((f) => {
          const list = byFolder[f.id] || [];
          if (list.length === 0) {
            // Aún así mostramos la carpeta vacía para poder moverle items
            return (
              <div key={f.id} className="text-xs">
                <div className="flex items-center gap-1 group">
                  <button
                    type="button"
                    onClick={() => toggleFolder(f.id)}
                    className="flex-1 text-left px-2 py-1.5 rounded font-extrabold text-[#1F2547] inline-flex items-center gap-1.5 hover:bg-[#F2F4FA]"
                    data-testid={`folder-${f.id}`}
                  >
                    <Folder size={12} style={{ color: f.color || "#A43790" }} />
                    {f.name}
                    <span className="text-[10px] text-[#6E7591] font-medium">(vacía)</span>
                  </button>
                  <button type="button" onClick={() => onRenameFolder(f)} className="opacity-0 group-hover:opacity-100 text-[#6E7591] hover:text-[#A43790] p-1" title="Renombrar"><Edit3 size={11} /></button>
                  <button type="button" onClick={() => onDeleteFolder(f)} className="opacity-0 group-hover:opacity-100 text-[#6E7591] hover:text-[#A52258] p-1" title="Eliminar"><Trash2 size={11} /></button>
                </div>
              </div>
            );
          }
          const open = openFolders[f.id] !== false; // por defecto abierta
          return (
            <div key={f.id}>
              <div className="flex items-center gap-1 group">
                <button
                  type="button"
                  onClick={() => toggleFolder(f.id)}
                  className="flex-1 text-left px-2 py-1.5 rounded font-extrabold text-[#1F2547] inline-flex items-center gap-1.5 hover:bg-[#F2F4FA]"
                  data-testid={`folder-${f.id}`}
                >
                  {open ? <FolderOpen size={12} style={{ color: f.color || "#A43790" }} /> : <Folder size={12} style={{ color: f.color || "#A43790" }} />}
                  {f.name}
                  <span className="text-[10px] text-[#6E7591] font-medium">({list.length})</span>
                </button>
                <button type="button" onClick={() => onRenameFolder(f)} className="opacity-0 group-hover:opacity-100 text-[#6E7591] hover:text-[#A43790] p-1" title="Renombrar"><Edit3 size={11} /></button>
                <button type="button" onClick={() => onDeleteFolder(f)} className="opacity-0 group-hover:opacity-100 text-[#6E7591] hover:text-[#A52258] p-1" title="Eliminar"><Trash2 size={11} /></button>
              </div>
              {open && (
                <div className="ml-3 space-y-1 mt-1 pl-2 border-l-2 border-[#E1E5F0]">
                  {list.map((t, i) => renderTpl(t, list, i))}
                </div>
              )}
            </div>
          );
        })}
        {/* Plantillas sin carpeta */}
        {(byFolder._unfiled || []).length > 0 && (
          <div className="pt-1 mt-1 border-t border-[#E1E5F0]">
            <p className="text-[10px] uppercase tracking-wider text-[#6E7591] font-extrabold px-2 py-1">Sin carpeta</p>
            <div className="space-y-1">
              {(byFolder._unfiled || []).map((t, i) => renderTpl(t, byFolder._unfiled || [], i))}
            </div>
          </div>
        )}
        {items.length === 0 && <p className="text-[11px] text-[#6E7591] p-3">Sin plantillas</p>}
      </div>
    </div>
  );
}

function TemplateViewer({ template, onEdit, canEdit }) {
  const [openBlock, setOpenBlock] = useState(0);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const triggerImport = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permitir reseleccionar el mismo archivo
    if (!file) return;
    if (!confirm(`¿Reemplazar los bloques actuales de "${template.code}" por el contenido de "${file.name}"?\n\nLas asignaciones existentes seguirán refiriendo a la versión nueva.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/questionnaire-templates/${template.code}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importado: ${data.blocks_imported} bloques · ${data.questions_imported} preguntas`);
      // Recargar página/parent invocando custom event simple
      window.dispatchEvent(new CustomEvent("tpl-refresh"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al importar el archivo");
    } finally {
      setImporting(false);
    }
  };

  const downloadExampleFile = async (fmt) => {
    try {
      const resp = await api.get("/questionnaire-template-example-file", {
        params: { fmt },
        responseType: "blob",
      });
      const ext = fmt === "xlsx" ? "xlsx" : "docx";
      const blob = new Blob([resp.data], {
        type: fmt === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_cuestionario_ejemplo.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo descargar el ejemplo");
    }
  };

  return (
    <div className="jasq-card p-6 space-y-5" data-testid="tpl-viewer">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">{template.code} · {template.kind?.toUpperCase()}</p>
          <h2 className="font-display text-2xl font-extrabold text-[#1F2547] truncate">{template.title}</h2>
          {template.subtitle && <p className="text-sm text-[#6E7591] mt-1">{template.subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => downloadExampleFile("docx")}
            data-testid="tpl-download-example-word"
            title="Descargar plantilla Word (.docx) de ejemplo"
            className="jasq-btn-ghost inline-flex items-center gap-2 text-sm">
            <Download size={14} /> Ejemplo Word
          </button>
          <button onClick={() => downloadExampleFile("xlsx")}
            data-testid="tpl-download-example-excel"
            title="Descargar plantilla Excel (.xlsx) de ejemplo"
            className="jasq-btn-ghost inline-flex items-center gap-2 text-sm">
            <Download size={14} /> Ejemplo Excel
          </button>
          <button onClick={triggerImport}
            data-testid="tpl-import-btn"
            disabled={!canEdit || importing}
            title={canEdit ? "Importar desde Word (.docx) o Excel (.xlsx)" : "Sólo Super Admin puede importar"}
            className="jasq-btn-blue inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Upload size={14} /> {importing ? "Importando…" : "Importar Word/Excel"}
          </button>
          <input ref={fileInputRef} type="file" accept=".docx,.xlsx,.xlsm" className="hidden"
            onChange={handleFileChange} data-testid="tpl-import-input" />
          <button
            data-testid="tpl-edit-btn"
            disabled={!canEdit}
            title={canEdit ? "Editar contenido" : "Sólo Super Admin puede editar"}
            onClick={onEdit}
            className="jasq-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Pencil size={14} /> Editar contenido
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <EditableInfoBox title="Mensaje de bienvenida" body={template.welcome_message} color="#2C49A0"
          canEdit={canEdit} code={template.code} field="welcome_message" rows={4} testid="tpl-welcome-box" />
        <EditableInfoBox title="Introducción" body={template.intro} color="#A43790"
          canEdit={canEdit} code={template.code} field="intro" rows={6} testid="tpl-intro-box" />
      </div>

      {template.important_note && (
        <div className="rounded-xl border-2 border-[#FFCC28] bg-[#FFF7DA] px-4 py-3 text-xs font-bold text-[#1F2547] whitespace-pre-line [overflow-wrap:anywhere]">
          ⓘ {template.important_note}
        </div>
      )}

      <div className="space-y-2">
        {(template.blocks || []).map((b, idx) => (
          <div key={b.id} className="rounded-xl border-2 border-[#E1E5F0] bg-white overflow-hidden">
            <button
              data-testid={`block-toggle-${b.id}`}
              onClick={() => setOpenBlock(openBlock === idx ? -1 : idx)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F7F8FA]"
            >
              <div className="flex items-center gap-3 min-w-0">
                {openBlock === idx ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1F2547] truncate">{b.title}</p>
                  <p className="text-[11px] text-[#6E7591] truncate">
                    {b.questions?.length || 0} preguntas · Escala: {b.scale_type || "—"}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold uppercase text-[#A43790]">Bloque {idx + 1}</span>
            </button>
            {openBlock === idx && (
              <div className="px-5 py-4 space-y-3 bg-[#FAFBFE] border-t border-[#E1E5F0]">
                {b.motivational_phrase && (
                  <div className="flex items-start gap-2 text-xs text-[#6E7591] italic">
                    <Quote size={12} className="mt-0.5 shrink-0 text-[#F5B82E]" />
                    {b.motivational_phrase}
                  </div>
                )}
                <ol className="space-y-2 list-decimal pl-5">
                  {(b.questions || []).map((q) => (
                    <li key={q.id} className="text-sm text-[#1F2547] leading-snug">
                      <span className="font-medium">{q.text}</span>
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#6E7591]">
                        [{q.type}{q.inverted ? " · INV" : ""}]
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// v5.7.9 · Bloque editable en línea (solo Super Admin): lápiz → texto → Guardar. Guarda con PATCH /questionnaire-templates/{code}.
function EditableInfoBox({ title, body, color, canEdit, code, field, rows = 4, testid }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(body || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setText(body || ""); }, [body, code, editing]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/questionnaire-templates/${code}`, { [field]: text.trim() });
      toast.success(`${title} actualizado`);
      setEditing(false);
      window.dispatchEvent(new CustomEvent("tpl-refresh"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };
  if (!editing) {
    return (
      <div className="rounded-xl border-2 border-[#E1E5F0] bg-white p-3 min-w-0 relative" data-testid={testid}>
        <p className="text-[10px] uppercase tracking-wider font-extrabold" style={{ color }}>{title}</p>
        <p className="text-sm text-[#1F2547] mt-1 leading-snug whitespace-pre-line [overflow-wrap:anywhere]">{body || "—"}</p>
        {canEdit && (
          <button type="button" onClick={() => setEditing(true)} title={`Editar ${title.toLowerCase()}`}
            aria-label={`Editar ${title}`} data-testid={`${testid}-edit`}
            className="absolute top-2 right-2 p-1.5 rounded-lg text-[#6E7591] hover:text-[#A43790] hover:bg-[#F7F8FA]">
            <Pencil size={13} />
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 border-[#A43790] bg-white p-3 min-w-0" data-testid={`${testid}-editor`}>
      <p className="text-[10px] uppercase tracking-wider font-extrabold" style={{ color }}>{title}</p>
      <textarea value={text} rows={rows} onChange={(e) => setText(e.target.value)} autoFocus
        className="jasq-input mt-1 w-full text-sm" data-testid={`${testid}-text`} />
      <div className="flex justify-end gap-2 mt-2">
        <button type="button" onClick={() => { setText(body || ""); setEditing(false); }} disabled={saving}
          className="jasq-btn-ghost text-xs">Cancelar</button>
        <button type="button" onClick={save} disabled={saving || !text.trim()} data-testid={`${testid}-save`}
          className="jasq-btn-primary text-xs disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
      </div>
    </div>
  );
}

function InfoBox({ title, body, color }) {
  return (
    <div className="rounded-xl border-2 border-[#E1E5F0] bg-white p-3 min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-extrabold" style={{ color }}>{title}</p>
      <p className="text-sm text-[#1F2547] mt-1 leading-snug whitespace-pre-line [overflow-wrap:anywhere]">{body}</p>
    </div>
  );
}

// ---------------- Editor ----------------
function TemplateEditor({ template, onChange, onSave, onCancel }) {
  const set = (patch) => onChange({ ...template, ...patch });

  const updateBlock = (idx, patch) => {
    const blocks = template.blocks.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    set({ blocks });
  };
  const addBlock = () => {
    const idx = template.blocks.length + 1;
    set({
      blocks: [
        ...template.blocks,
        {
          id: `b${idx}`,
          order: idx,
          title: `Bloque ${idx} · Nuevo`,
          motivational_phrase: "",
          scale_type: "likert_agree",
          questions: [],
        },
      ],
    });
  };
  const removeBlock = (idx) => {
    if (!confirm("¿Eliminar este bloque?")) return;
    set({ blocks: template.blocks.filter((_, i) => i !== idx) });
  };
  const moveBlock = (idx, dir) => {
    const blocks = [...template.blocks];
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
    set({ blocks });
  };

  const updateQuestion = (bi, qi, patch) => {
    const blocks = template.blocks.map((b, i) => {
      if (i !== bi) return b;
      const qs = b.questions.map((q, j) => (j === qi ? { ...q, ...patch } : q));
      return { ...b, questions: qs };
    });
    set({ blocks });
  };
  const addQuestion = (bi) => {
    const blocks = template.blocks.map((b, i) => {
      if (i !== bi) return b;
      const next = b.questions.length + 1;
      return {
        ...b,
        questions: [
          ...b.questions,
          {
            id: `${b.id}-q${next}`,
            order: next,
            text: "Nueva pregunta",
            type: b.scale_type === "open" ? "open" : (b.scale_type === "scale_1_10" ? "scale_1_10" : "likert"),
            scale: b.scale_type === "open" ? null : b.scale_type,
            inverted: false,
            required: true,
          },
        ],
      };
    });
    set({ blocks });
  };
  const removeQuestion = (bi, qi) => {
    const blocks = template.blocks.map((b, i) => {
      if (i !== bi) return b;
      return { ...b, questions: b.questions.filter((_, j) => j !== qi) };
    });
    set({ blocks });
  };

  return (
    <div className="jasq-card p-6 space-y-5" data-testid="tpl-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Editando · {template.code}</p>
          <h2 className="font-display text-xl font-extrabold text-[#1F2547]">{template.title}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="jasq-btn-ghost inline-flex items-center gap-2" data-testid="tpl-cancel">
            <X size={14} /> Cancelar
          </button>
          <button onClick={onSave} className="jasq-btn-primary inline-flex items-center gap-2" data-testid="tpl-save">
            <Save size={14} /> Guardar cambios
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Título">
          <input value={template.title} onChange={(e) => set({ title: e.target.value })}
            className="jasq-input" data-testid="tpl-title" />
        </Field>
        <Field label="Subtítulo">
          <input value={template.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })}
            className="jasq-input" data-testid="tpl-subtitle" />
        </Field>
      </div>

      <Field label="Mensaje de bienvenida (Zarek)">
        <textarea value={template.welcome_message || ""} rows={2}
          onChange={(e) => set({ welcome_message: e.target.value })}
          className="jasq-input" data-testid="tpl-welcome" />
      </Field>

      <Field label="Introducción">
        <textarea value={template.intro || ""} rows={3}
          onChange={(e) => set({ intro: e.target.value })}
          className="jasq-input" data-testid="tpl-intro" />
      </Field>

      <Field label="Nota importante (opcional)">
        <input value={template.important_note || ""} onChange={(e) => set({ important_note: e.target.value })}
          className="jasq-input" />
      </Field>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-extrabold text-[#1F2547]">Bloques de preguntas</h3>
          <button onClick={addBlock} className="jasq-btn-blue inline-flex items-center gap-2 text-sm" data-testid="add-block">
            <Plus size={14} /> Añadir bloque
          </button>
        </div>

        {template.blocks.map((b, bi) => (
          <div key={b.id} className="rounded-xl border-2 border-[#E1E5F0] bg-white p-4 space-y-3">
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-1 mt-1">
                <button onClick={() => moveBlock(bi, -1)} className="text-[#6E7591] hover:text-[#1F2547]" title="Subir">▲</button>
                <button onClick={() => moveBlock(bi, +1)} className="text-[#6E7591] hover:text-[#1F2547]" title="Bajar">▼</button>
              </div>
              <div className="flex-1 space-y-2">
                <div className="grid sm:grid-cols-[1fr_180px] gap-2">
                  <input value={b.title} onChange={(e) => updateBlock(bi, { title: e.target.value })}
                    className="jasq-input font-bold" placeholder="Título del bloque"
                    data-testid={`block-${bi}-title`} />
                  <select value={b.scale_type || ""} onChange={(e) => updateBlock(bi, { scale_type: e.target.value })}
                    className="jasq-input" data-testid={`block-${bi}-scale`}>
                    {SCALES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <input value={b.motivational_phrase || ""} onChange={(e) => updateBlock(bi, { motivational_phrase: e.target.value })}
                  className="jasq-input italic" placeholder="Frase motivadora del bloque" />
              </div>
              <button onClick={() => removeBlock(bi)} title="Eliminar bloque"
                className="text-[#A52258] hover:text-[#D63384]"><Trash2 size={16} /></button>
            </div>

            <div className="pl-7 space-y-2">
              {(b.questions || []).map((q, qi) => (
                <div key={q.id} className="grid sm:grid-cols-[1fr_140px_70px_40px] gap-2 items-center">
                  <input value={q.text} onChange={(e) => updateQuestion(bi, qi, { text: e.target.value })}
                    className="jasq-input text-sm" data-testid={`q-${bi}-${qi}-text`} />
                  <select value={q.type} onChange={(e) => {
                      const type = e.target.value;
                      updateQuestion(bi, qi, {
                        type,
                        scale: type === "likert" ? (b.scale_type === "open" ? "likert_agree" : b.scale_type) : (type === "scale_1_10" ? "scale_1_10" : null),
                      });
                    }}
                    className="jasq-input text-xs">
                    {QTYPE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                  <label className="text-[11px] font-bold inline-flex items-center gap-1 text-[#A43790]">
                    <input type="checkbox" checked={!!q.inverted}
                      onChange={(e) => updateQuestion(bi, qi, { inverted: e.target.checked })} />
                    INV
                  </label>
                  <button onClick={() => removeQuestion(bi, qi)} className="text-[#A52258] hover:text-[#D63384]" title="Eliminar pregunta">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button onClick={() => addQuestion(bi)} className="text-xs font-bold text-[#2C49A0] inline-flex items-center gap-1 hover:underline"
                data-testid={`add-q-${bi}`}>
                <Plus size={12} /> Añadir pregunta
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
